import React from "react";

const ScoreBanner = ({ result }) => {
  if (!result) {
    return null;
  }

  const scorePercentage = result.scorePercentage || 0;
  const isPass = scorePercentage >= 50;

  // Calculate time taken
  const calculateTimeTaken = () => {
    if (!result.startedAt || !result.submittedAt) return null;

    try {
      const startTime = new Date(result.startedAt);
      const endTime = new Date(result.submittedAt);
      const diffMs = endTime - startTime;
      const diffMinutes = Math.round(diffMs / (1000 * 60));
      return diffMinutes;
    } catch (error) {
      return null;
    }
  };

  const timeTaken = calculateTimeTaken();

  return (
    <div
      style={{
        background: isPass ? "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)" : "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
        border: `3px solid ${isPass ? "#059669" : "#dc2626"}`,
        borderRadius: "16px",
        padding: "32px 24px",
        marginBottom: "32px",
        textAlign: "center",
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative background element */}
      <div
        style={{
          position: "absolute",
          top: "-50%",
          right: "-20%",
          width: "200px",
          height: "200px",
          borderRadius: "50%",
          background: isPass ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)",
          zIndex: 0,
        }}
      />

      <div style={{ position: "relative", zIndex: 1 }}>
        {/* Score Display */}
        <div
          style={{
            fontSize: "56px",
            fontWeight: 900,
            color: isPass ? "#047857" : "#b91c1c",
            marginBottom: "16px",
            textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          {scorePercentage.toFixed(1)}%
        </div>

        {/* Title */}
        <h2
          style={{
            fontSize: "24px",
            fontWeight: 800,
            color: "#1f2937",
            margin: "0 0 12px",
            letterSpacing: "-0.025em",
          }}
        >
          {isPass ? "🎉 Excellent Work!" : "📚 Keep Practicing!"}
        </h2>

        {/* Message */}
        <p
          style={{
            color: "#4b5563",
            fontSize: "16px",
            margin: "0 0 24px",
            fontWeight: 500,
            lineHeight: 1.5,
          }}
        >
          {result.message}
        </p>

        {/* Test Summary Information */}
        <div
          style={{
            background: "rgba(255, 255, 255, 0.8)",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: 700,
              color: "#374151",
              margin: "0 0 16px",
              textAlign: "center",
            }}
          >
            📊 Test Summary
          </h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "12px",
            }}
          >
            {result.testName && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 12px",
                  background: "rgba(255, 255, 255, 0.6)",
                  borderRadius: "8px",
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                }}
              >
                <span style={{ fontWeight: 600, color: "#374151", fontSize: "14px" }}>Test:</span>
                <span style={{ color: "#1f2937", fontSize: "14px", fontWeight: 500 }}>{result.testName}</span>
              </div>
            )}

            {result.attemptNumber && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 12px",
                  background: "rgba(255, 255, 255, 0.6)",
                  borderRadius: "8px",
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                }}
              >
                <span style={{ fontWeight: 600, color: "#374151", fontSize: "14px" }}>Attempt:</span>
                <span style={{ color: "#1f2937", fontSize: "14px", fontWeight: 500 }}>#{result.attemptNumber}</span>
              </div>
            )}

            {result.durationMinutes && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 12px",
                  background: "rgba(255, 255, 255, 0.6)",
                  borderRadius: "8px",
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                }}
              >
                <span style={{ fontWeight: 600, color: "#374151", fontSize: "14px" }}>Duration:</span>
                <span style={{ color: "#1f2937", fontSize: "14px", fontWeight: 500 }}>{result.durationMinutes} min</span>
              </div>
            )}

            {timeTaken !== null && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 12px",
                  background: "rgba(255, 255, 255, 0.6)",
                  borderRadius: "8px",
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                }}
              >
                <span style={{ fontWeight: 600, color: "#374151", fontSize: "14px" }}>Time Taken:</span>
                <span style={{ color: "#1f2937", fontSize: "14px", fontWeight: 500 }}>{timeTaken} min</span>
              </div>
            )}

            {result.startedAt && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 12px",
                  background: "rgba(255, 255, 255, 0.6)",
                  borderRadius: "8px",
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                }}
              >
                <span style={{ fontWeight: 600, color: "#374151", fontSize: "14px" }}>Started:</span>
                <span style={{ color: "#1f2937", fontSize: "14px", fontWeight: 500 }}>
                  {new Date(result.startedAt).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            )}

            {result.submittedAt && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 12px",
                  background: "rgba(255, 255, 255, 0.6)",
                  borderRadius: "8px",
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                }}
              >
                <span style={{ fontWeight: 600, color: "#374151", fontSize: "14px" }}>Submitted:</span>
                <span style={{ color: "#1f2937", fontSize: "14px", fontWeight: 500 }}>
                  {new Date(result.submittedAt).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Statistics Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            gap: "16px",
            marginTop: "24px",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
              borderRadius: "12px",
              padding: "16px 12px",
              border: "2px solid #3b82f6",
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)",
            }}
          >
            <div style={{ fontSize: "12px", color: "#1e40af", marginBottom: "6px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Total
            </div>
            <div
              style={{
                fontSize: "28px",
                fontWeight: 800,
                color: "#1e40af",
              }}
            >
              {result.totalQuestions}
            </div>
          </div>

          <div
            style={{
              background: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
              borderRadius: "12px",
              padding: "16px 12px",
              border: "2px solid #16a34a",
              boxShadow: "0 4px 12px rgba(22, 163, 74, 0.15)",
            }}
          >
            <div style={{ fontSize: "12px", color: "#166534", marginBottom: "6px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Correct
            </div>
            <div
              style={{
                fontSize: "28px",
                fontWeight: 800,
                color: "#166534",
              }}
            >
              {result.correctAnswers}
            </div>
          </div>

          <div
            style={{
              background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
              borderRadius: "12px",
              padding: "16px 12px",
              border: "2px solid #d97706",
              boxShadow: "0 4px 12px rgba(217, 119, 6, 0.15)",
            }}
          >
            <div style={{ fontSize: "12px", color: "#92400e", marginBottom: "6px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Answered
            </div>
            <div
              style={{
                fontSize: "28px",
                fontWeight: 800,
                color: "#92400e",
              }}
            >
              {result.answeredQuestions}
            </div>
          </div>

          <div
            style={{
              background: "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
              borderRadius: "12px",
              padding: "16px 12px",
              border: "2px solid #dc2626",
              boxShadow: "0 4px 12px rgba(220, 38, 38, 0.15)",
            }}
          >
            <div style={{ fontSize: "12px", color: "#991b1b", marginBottom: "6px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Incorrect
            </div>
            <div
              style={{
                fontSize: "28px",
                fontWeight: 800,
                color: "#991b1b",
              }}
            >
              {(result.answeredQuestions || 0) - (result.correctAnswers || 0)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoreBanner;

